import { useAppContext } from '@/core';
import { useUserPermissions } from '@/core/hooks/useUserPermissions';
import { routeConfigs, RouteConfig } from '@/config/routes.config';
import { Layout } from 'antd';
import { find, flatMapDeep, get } from 'lodash';
import React, { memo, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { MenuProps } from '../Menu';
import { Menu } from '../Menu';
import { config } from '@/config';
type Props = {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  setTitle: React.Dispatch<React.SetStateAction<string>>;
};

type MenuItem = Required<MenuProps>['items'][number];

const getItem = (
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[] | undefined | null,
): MenuItem => {
  return {
    key,
    icon,
    children,
    title: String(label),
    label,
  } as MenuItem;
};
const InternalSidebar = (props: Props) => {
  const { collapsed, setCollapsed, setTitle } = props;
  const { Sider } = Layout;
  const navigate = useNavigate();
  const { t } = useAppContext();
  const { hasPermission } = useUserPermissions();

  const filterRoutesByPermission = (routes: RouteConfig[]): RouteConfig[] => {
    return routes
      .filter((route) => {
        if (!hasPermission(route.permission)) {
          return false;
        }

        // If it has children, filter the children as well
        if (route.children) {
          const filteredChildren = filterRoutesByPermission(route.children);
          // Only include parent if it has accessible children
          return filteredChildren.length > 0;
        }

        return true;
      })
      .map((route) => ({
        ...route,
        children: route.children ? filterRoutesByPermission(route.children) : undefined,
      }));
  };

  /* eslint-disable indent */
  const buildMenuItems = (routes: RouteConfig[]): MenuItem[] => {
    return routes.map((route) => {
      const IconComponent = route.icon;
      const icon = IconComponent
        ? React.createElement(IconComponent, {
            style: collapsed ? { fontSize: '24px' } : { fontSize: '18px' },
          })
        : undefined;

      const children = route.children ? buildMenuItems(route.children) : undefined;

      return getItem(t(route.label), route.path, icon, children);
    });
  };

  const items = useMemo<MenuItem[]>(() => {
    const filteredRoutes = filterRoutesByPermission(routeConfigs);
    return buildMenuItems(filteredRoutes);
  }, [collapsed, hasPermission, t]);

  const getChildren = (initItem: any): any => {
    const item = { ...initItem };
    delete item.children;
    delete item.icon;
    delete item.label;
    if (!initItem.children || !initItem.children.length) {
      return item;
    }
    return [item, flatMapDeep(initItem.children, getChildren)];
  };
  const flattenMenuItems = useMemo(() => flatMapDeep(items, getChildren), [items]);

  useEffect(() => {
    const foundItem = find(flattenMenuItems, { key: location.pathname });
    if (foundItem) setTitle(get(foundItem, 'title'));
  }, [location.pathname]);

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={(value) => setCollapsed(value)}
      style={{ background: '#001529' }}
      width={236}
      className='[&_.ant-layout-sider-children]:flex [&_.ant-layout-sider-children]:flex-col h-[100dvh] !sticky top-0'
    >
      <div className='sticky top-0'>
        <Link to='/' className='text-white flex items-center h-[66px] gap-[10px] shrink-0 px-3'>
          {collapsed ? (
            <img src='/favicon.png' alt='logo' className='w-10 h-10 mx-auto' />
          ) : (
            <div>
              <div className='w-full flex gap-3'>
                <img src='/favicon.png' alt='logo' className='w-9 h-9' />
                <h1 className='font-sans text-white text-lg mt-2'>MindX</h1>
              </div>
            </div>
          )}
        </Link>
        <Menu
          className='px-1 [&_.ant-menu-sub.ant-menu-inline]:!bg-sky-900/10 [&_.ant-menu-sub.ant-menu-inline]:rounded [&_.ant-menu-item-icon]:!text-[16px]'
          onClick={(onClickParams) => {
            const { key: path } = onClickParams;
            navigate(path);
          }}
          onSelect={(onSelectParams) => {
            const labelSelect = items.find((i) => {
              return i?.key === onSelectParams.key
                ? true
                : get(i, 'children', []).some((item) => item?.key === onSelectParams.key);
            });
            setTitle(get(labelSelect, 'label', '') as unknown as string);
          }}
          theme='dark'
          selectedKeys={[location.pathname]}
          inlineIndent={12}
          mode='inline'
          items={items}
        />
      </div>
    </Sider>
  );
};

export const Sidebar = memo(InternalSidebar);
